import { Injectable, inject } from "@angular/core";
import { Router } from "@angular/router";
import { AlertController, AlertOptions } from "@ionic/angular";
import { LicenseTypes } from "@models/MasterData";
import { GlobalVarsModel } from "@models/ModelGlobalsVar";
import { DataUserMSOService } from "@mso-services/dataUserMSO.services";
import { TranslateService } from "@ngx-translate/core";
import { MSO_PATHS } from "@shared-constants/routes";
import { EventsService } from "./EventsService";
import { NewBaseService } from "./newBase.service";

@Injectable({ providedIn: "root" })
export class VerificationAlerts {
  globalVars!: GlobalVarsModel;
  newBaseService = inject(NewBaseService);
  router = inject(Router);
  dataUser = inject(DataUserMSOService);

  constructor(
    public translate: TranslateService,
    public events: EventsService,
    public alertCtrl: AlertController,
  ) {
    this.newBaseService.getVars.subscribe((data: GlobalVarsModel) => {
      this.globalVars = data;
    });
  }

  async showPopupDNIExpired(days?: number) {
    let msg = "";
    const daysSinceFirstCheck = days || this.dataUser.getExtraUserData()?.dataDNIExpired?.daysSinceFirstCheck;
    if (this.hasToBeBlockedByDni(daysSinceFirstCheck)) {
      msg =
        this.globalVars?.LICENSE_TYPE === LicenseTypes.Colombia
          ? this.translate.instant("registereddocument")
          : this.translate.instant("notificationplayersafter45");
      this.globalVars.extraUserData.blockDeposits = true;
      this.globalVars.extraUserData.blockWithdrawals = this.globalVars?.LICENSE_TYPE !== LicenseTypes.Colombia;
      this.globalVars.extraUserData.blockBets = this.globalVars?.LICENSE_TYPE === LicenseTypes.Colombia;
    } else {
      msg =
        this.globalVars?.LICENSE_TYPE === LicenseTypes.Colombia
          ? this.translate.instant("remindregistereddocument")
          : this.translate.instant("notificationplayersbefore45");
    }
    const alertOpt: AlertOptions = {
      header: `${this.translate.instant("playresponsibly")}`,
      cssClass: "verificationAlert",
      message: msg,
      buttons: [
        {
          text: this.translate.instant("updatenow"),
          handler: () => {
            this.events.publish("documentverification:open");
          },
        },
      ],
    };
    return this.presentAlert(alertOpt);
  }

  private hasToBeBlockedByDni(daysSinceFirstCheck: number): boolean {
    const isColombia = this.globalVars?.LICENSE_TYPE === LicenseTypes.Colombia;
    const blockUser = (isColombia && daysSinceFirstCheck >= 90) || daysSinceFirstCheck >= 30;
    return blockUser;
  }

  async showPopupProfesion(days?: number) {
    const daysSinceFirstCheck = days || this.globalVars.extraUserData.daysSinceFirstCheckProf;
    const alertOpt: AlertOptions = {
      header: this.translate.instant("Attention"),
      cssClass: "verificationAlert",
      message: this.translate.instant("V2_profesionRequest"),
      buttons: [
        {
          cssClass: "confirmBtnProfesion",
          role: "confirm",
          handler: () => {
            this.router.navigateByUrl(`${MSO_PATHS.BASE}/${MSO_PATHS.PersonalInfoPage}`);
          },
          text: this.translate.instant("Update"),
        },
      ],
    };
    if (daysSinceFirstCheck < 45) {
      alertOpt.buttons.unshift({
        cssClass: "cancelBtnProfesion",
        role: "cancel",
        text: "",
      });
    } else {
      this.globalVars.extraUserData.blockDeposits = true;
      this.globalVars.extraUserData.blockWithdrawals = true;
      alertOpt.message = this.translate.instant("professionalverificationprocess");
    }

    return this.presentAlert(alertOpt);
  }

  isDniExpired() {
    return (
      this.globalVars.FEATURES.MSO_CheckDNIExpired &&
      this.dataUser.getExtraUserData()?.dataDNIExpired?.isDniExpired
    );
  }

  isProfesionRequired() {
    return this.globalVars.FEATURES.MSO_CheckProfesion && !this.globalVars.extraUserData.haveProfesion;
  }

  public checkDNIExpired() {
    if (this.isDniExpired()) {
      this.showPopupDNIExpired();
    }
  }

  private async presentAlert(alertOpt: AlertOptions): Promise<any> {
    const alert = await this.alertCtrl.create(alertOpt);
    const alertHeader = alert.querySelector("h2");
    const imgHeader = document.createElement("img");
    imgHeader.classList.add("icon-circle-info");
    imgHeader.src = "assets/global/img/icon-circle-info.svg";
    alertHeader.parentNode.insertBefore(imgHeader, alertHeader);
    await alert.present();
    return new Promise((resolve, _) => {
      alert.onDidDismiss().then(({ data, role }) => {
        resolve({ data, role });
      });
    });
  }
}
